import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-forest-green mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-earth max-w-2xl mx-auto">
            Begin your journey to wellness. Reach out for a consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-serif font-bold text-forest-green mb-6">
                Visit Our Clinic
              </h3>
              <div className="space-y-6">
                <Card className="border-none bg-cream/50 hover:bg-cream transition-all duration-300">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage-green rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest-green mb-1">Phone</p>
                      <a href={`tel:${contactInfo.phone}`} className="text-earth hover:text-forest-green transition-colors">
                        {contactInfo.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-cream/50 hover:bg-cream transition-all duration-300">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest-green mb-1">WhatsApp</p>
                      <a 
                        href={`https://wa.me/${contactInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-earth hover:text-green-600 transition-colors"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-cream/50 hover:bg-cream transition-all duration-300">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage-green rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest-green mb-1">Email</p>
                      <p className="text-earth">{contactInfo.email}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-cream/50 hover:bg-cream transition-all duration-300">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage-green rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest-green mb-1">Address</p>
                      <a 
                        href={contactInfo.location.mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-earth hover:text-forest-green transition-colors"
                      >
                        {contactInfo.address}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-cream/50 hover:bg-cream transition-all duration-300">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage-green rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-forest-green mb-1">Hours</p>
                      <p className="text-earth">{contactInfo.hours}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="border-none bg-gradient-to-br from-sage-green/5 to-cream shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-serif font-bold text-forest-green mb-6">
                  Send Us a Message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-forest-green mb-2">
                      Full Name
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-forest-green mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-forest-green mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      required
                      className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-forest-green mb-2">
                      Message
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your health concerns..."
                      rows={4}
                      required
                      className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-sage-green hover:bg-forest-green text-white font-semibold py-6 transition-all duration-300 transform hover:scale-105"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
